import { useEffect, useRef, useState } from 'react';
import { createPortal } from 'react-dom';
import { useNavigate } from 'react-router-dom';
import { QRCodeSVG } from 'qrcode.react';
import { ArrowLeft, Coffee, Printer } from 'lucide-react';
import { useAuthStore } from '../../store/authStore';
import { getShopByOwner, type Shop } from '../../services/shop-service';
import { apiFetch } from '../../services/api-client';
import { liteErrorCopy, type LiteMember } from './LiteMemberPanel';
import OwnerShell, { OwnerLoadingShell } from './OwnerShell';
import CoralHeader from '../dashboard/CoralHeader';

// Printed cards for "lite" (walletless) members — Task 9's O1 sheet. Some
// shops keep handing out the old paper stamp cards; this reprints them with
// a QR the counter can scan straight into the same lite-member flow that
// Counter Book (Task 8) and the scan modal (Task 7) already use.
//
// Same print gotcha as TillKit: the global `@media print` rule (index.css)
// hides every direct <body> child except one with class `print-qr-view`, so
// the printable sheet is rendered TWICE — once inline as an on-screen
// preview, and once portaled straight to document.body while printing.

const STAMP_SLOTS = 10; // mirrors CARD_SIZE in api/_lib/lite.ts
const MIN_BATCH = 1;
const MAX_BATCH = 20;
const DEFAULT_BATCH = 8; // one full 8-up sheet

function clampBatch(n: number): number {
  if (Number.isNaN(n)) return MIN_BATCH;
  return Math.min(MAX_BATCH, Math.max(MIN_BATCH, Math.round(n)));
}

function MemberCard({ shop, member }: { shop: Shop; member: LiteMember }) {
  return (
    <div className="member-card" data-card-code={member.card_code}>
      {shop.image_url && <img src={shop.image_url} alt="" className="member-card-logo" />}
      <p className="member-card-shop">{shop.name}</p>
      <div className="member-card-qr">
        <QRCodeSVG
          value={`bitbrew:lite:${member.card_code}`}
          size={104}
          level="M"
          bgColor="#ffffff"
          fgColor="#000000"
          includeMargin
        />
      </div>
      {member.display_name ? (
        <p className="member-card-name">{member.display_name}</p>
      ) : (
        <div className="member-card-name-blank" aria-hidden="true" />
      )}
      <div className="member-card-stamps">
        {Array.from({ length: STAMP_SLOTS }).map((_, i) => (
          <span key={i} className="member-card-stamp-slot" />
        ))}
      </div>
      <p className="member-card-footer">MEMBER CARD · SCAN AT THE COUNTER</p>
    </div>
  );
}

// Scoped print styles for the 8-up A4 sheet. Kept local to this component
// (rather than added to global index.css) since nothing else uses this
// layout — same "small scoped block" approach TillKit uses for its own
// print-only rules.
function CardSheetStyle() {
  return (
    <style>{`
      @page { size: A4; margin: 10mm; }
      .member-card-sheet {
        display: grid;
        grid-template-columns: repeat(2, 1fr);
        gap: 6mm;
      }
      .member-card {
        border: 1px solid #000;
        border-radius: 4px;
        padding: 4mm;
        display: flex;
        flex-direction: column;
        align-items: center;
        text-align: center;
        background: #fff;
        color: #000;
        break-inside: avoid;
        page-break-inside: avoid;
      }
      /* Force a clean page break after every 8th card so the sheet
         paginates predictably past one A4 page (2 cols x 4 rows = 8). */
      .member-card:nth-child(8n) {
        break-after: page;
        page-break-after: always;
      }
      .member-card-logo {
        width: 10mm;
        height: 10mm;
        object-fit: cover;
        margin-bottom: 2mm;
      }
      .member-card-shop {
        font-size: 10pt;
        font-weight: 700;
        text-transform: uppercase;
        margin: 0 0 2mm;
      }
      .member-card-qr {
        padding: 1mm;
        border: 1px solid #000;
        background: #fff;
      }
      .member-card-name {
        font-size: 8pt;
        font-weight: 600;
        margin: 2mm 0 0;
        max-width: 100%;
        overflow: hidden;
        text-overflow: ellipsis;
        white-space: nowrap;
      }
      .member-card-name-blank {
        width: 80%;
        border-bottom: 1px solid #000;
        height: 3mm;
        margin: 3mm 0 0;
      }
      .member-card-stamps {
        display: grid;
        grid-template-columns: repeat(5, 1fr);
        gap: 1mm;
        width: 80%;
        margin-top: 2mm;
      }
      .member-card-stamp-slot {
        aspect-ratio: 1;
        border: 1px solid #000;
        border-radius: 50%;
      }
      .member-card-footer {
        font-size: 5pt;
        letter-spacing: 0.05em;
        margin: 2mm 0 0;
        color: #333;
      }
    `}</style>
  );
}

function CardSheet({ shop, members }: { shop: Shop; members: LiteMember[] }) {
  return (
    <>
      <CardSheetStyle />
      <div className="member-card-sheet">
        {members.map((m) => (
          <MemberCard key={m.id} shop={shop} member={m} />
        ))}
      </div>
    </>
  );
}

export default function MemberCards() {
  const { userAddress } = useAuthStore();
  const navigate = useNavigate();

  const [shop, setShop] = useState<Shop | null>(null);
  const [shopLoading, setShopLoading] = useState(true);

  const [members, setMembers] = useState<LiteMember[]>([]);
  const [membersLoading, setMembersLoading] = useState(false);
  const [listError, setListError] = useState<string | null>(null);
  const [selected, setSelected] = useState<Set<string>>(new Set());
  const membersRef = useRef<LiteMember[]>([]);

  const [batchCount, setBatchCount] = useState(DEFAULT_BATCH);
  const [batchBusy, setBatchBusy] = useState(false);
  const [batchError, setBatchError] = useState<string | null>(null);

  const [printing, setPrinting] = useState(false);

  useEffect(() => {
    if (!userAddress) return;
    let cancelled = false;
    getShopByOwner(userAddress)
      .then((s) => { if (!cancelled) setShop(s); })
      .finally(() => { if (!cancelled) setShopLoading(false); });
    return () => { cancelled = true; };
  }, [userAddress]);

  async function loadMembers(shopId: string, opts?: { selectNewOnly?: boolean }) {
    setMembersLoading(true);
    setListError(null);
    const prevIds = new Set(membersRef.current.map((m) => m.id));
    try {
      const r = await apiFetch<{ members: LiteMember[] }>('/api/stamp', {
        action: 'lite-list', shopId, query: '',
      });
      membersRef.current = r.members;
      setMembers(r.members);
      if (opts?.selectNewOnly) {
        setSelected((prev) => {
          const next = new Set(prev);
          for (const m of r.members) if (!prevIds.has(m.id)) next.add(m.id);
          return next;
        });
      } else {
        setSelected(new Set(r.members.map((m) => m.id)));
      }
    } catch (e) {
      setListError(liteErrorCopy(e, 'COULD NOT LOAD MEMBERS'));
    } finally {
      setMembersLoading(false);
    }
  }

  useEffect(() => {
    if (shop) loadMembers(shop.id);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [shop]);

  function toggle(id: string) {
    setSelected((prev) => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id); else next.add(id);
      return next;
    });
  }

  async function createBlankBatch() {
    if (!shop) return;
    setBatchBusy(true);
    setBatchError(null);
    try {
      await apiFetch('/api/stamp', {
        action: 'lite-create', shopId: shop.id, count: batchCount,
      });
      await loadMembers(shop.id, { selectNewOnly: true });
    } catch (e) {
      setBatchError(liteErrorCopy(e, 'COULD NOT CREATE CARDS'));
    } finally {
      setBatchBusy(false);
    }
  }

  function handlePrint() {
    setPrinting(true);
    // Let the portal mount before opening the print dialog.
    setTimeout(() => {
      window.print();
      setPrinting(false);
    }, 100);
  }

  const selectedMembers = members.filter((m) => selected.has(m.id));

  if (shopLoading) {
    return (
      <OwnerLoadingShell active="more" statusLabel="MORE" title="Loading…">
        <div className="bg-white rounded-[10px] h-64 animate-pulse" />
      </OwnerLoadingShell>
    );
  }

  return (
    <OwnerShell
      active="more"
      header={<CoralHeader statusLabel="MORE" eyebrow="OWNER TOOLS" title="Member Cards" />}
    >
      <div className="space-y-3.5 pb-8">
        {/* sr-only: real landmark heading; CoralHeader's title already shows
            the page name visually. */}
        <h1 className="sr-only">Member Cards</h1>

        <button
          onClick={() => navigate('/shops/owner/dashboard')}
          className="font-grotesk font-bold text-[13px] text-body-grey hover:text-coral py-2 flex items-center gap-2"
        >
          <ArrowLeft size={13} /> Back to dashboard
        </button>

        <div className="bg-white rounded-[10px] p-[18px]">
          <p className="font-grotesk font-bold text-[17px] text-primary mb-[5px]">Member Cards</p>
          <p className="font-grotesk font-medium text-[13px] leading-[1.4] text-body-grey mb-4">
            Print QR cards for walletless members — hand them out, or reprint the counter box.
            Each card scans straight into the Counter Book flow.
          </p>

          <div className="flex items-center gap-2 mb-3">
            <label htmlFor="batch-count" className="font-heading text-[8px] tracking-[1px] uppercase text-body-grey">
              BLANK CARD COUNT
            </label>
            <input
              id="batch-count"
              aria-label="Blank card count"
              type="number"
              min={MIN_BATCH}
              max={MAX_BATCH}
              value={batchCount}
              onChange={(e) => setBatchCount(clampBatch(Number(e.target.value)))}
              className="w-14 rounded-[8px] bg-white border-2 border-hairline font-grotesk font-bold text-[13px] text-primary text-center py-1"
            />
            <button
              onClick={createBlankBatch}
              disabled={!shop || batchBusy}
              className="rounded-full bg-white border-2 border-hairline text-body-grey font-grotesk font-bold text-[13px] py-3 px-5 disabled:opacity-50"
            >
              {batchBusy ? 'Creating…' : `Print ${batchCount} blank cards`}
            </button>
          </div>
          {batchError && <p className="font-grotesk font-medium text-[13px] text-error mb-3">{batchError}</p>}

          <button
            onClick={handlePrint}
            disabled={!shop || selectedMembers.length === 0}
            className="rounded-full bg-coral text-white font-grotesk font-bold text-sm py-3 px-5 flex items-center gap-2 disabled:opacity-30"
          >
            <Printer size={14} /> Print
          </button>
        </div>

        {!shop && (
          <div className="bg-white rounded-[10px] p-8 text-center">
            <Coffee size={40} className="mx-auto mb-4 text-coral" />
            <p className="font-heading text-[10px] text-body-grey mb-4">NO SHOP REGISTERED</p>
            <a
              href="/shops/register"
              className="inline-block rounded-full bg-coral text-white font-grotesk font-bold text-sm py-3 px-6"
            >
              Register my shop
            </a>
          </div>
        )}

        {shop && (
          <div className="bg-white rounded-[10px] p-[18px]">
            {listError ? (
              <p className="font-grotesk font-medium text-[13px] text-error py-6 text-center">{listError}</p>
            ) : membersLoading && members.length === 0 ? (
              <p className="font-heading text-[10px] text-body-grey py-6 text-center">LOADING…</p>
            ) : members.length === 0 ? (
              <p className="font-heading text-[10px] text-body-grey py-6 text-center">
                NO MEMBER CARDS YET — PRINT SOME BLANK ONES ABOVE
              </p>
            ) : (
              <ul className="space-y-2" data-testid="member-list">
                {members.map((m) => (
                  <li key={m.id} className="flex items-center gap-2 px-3 py-2.5 rounded-[8px] border-2 border-hairline">
                    <input
                      type="checkbox"
                      checked={selected.has(m.id)}
                      onChange={() => toggle(m.id)}
                      className="shrink-0"
                    />
                    <span className="font-grotesk font-bold text-[13px] text-primary truncate flex-1">
                      {m.display_name || 'UNNAMED CARD'}
                    </span>
                    <span className="font-heading text-[8px] tracking-[1px] shrink-0 text-[#2f9440]">
                      {m.stamps} STAMPS
                    </span>
                  </li>
                ))}
              </ul>
            )}
          </div>
        )}

        {/* On-screen preview of the printed sheet, so staff can see what
            they're about to print before hitting the button. */}
        {shop && selectedMembers.length > 0 && (
          <div className="rounded-[10px] overflow-hidden border-2 border-hairline p-4">
            <CardSheet shop={shop} members={selectedMembers} />
          </div>
        )}
      </div>

      {/* Print portal — must be a direct <body> child for the print CSS
          (`@media print` in index.css hides everything else). */}
      {printing && shop &&
        createPortal(
          <div className="print-qr-view fixed inset-0 z-[200] bg-white overflow-auto">
            <CardSheet shop={shop} members={selectedMembers} />
          </div>,
          document.body,
        )}
    </OwnerShell>
  );
}
